import { AddFilledIcon, LoadSpinner } from '@calizahq/react-components-v2'
import { Table } from '@shared/components'
import React, { useMemo } from 'react'

import { BottomSheet, NoResults } from '@/components'
import { useMobile, useToggle } from '@/hooks'
import { HomeQualityCheckStatus } from '@/types/HomeQualityCheck'
import { HomeTimeline } from '@/types/Homes'

import { CreateHomeQualityCheckInspection } from '../Inspections/CreateInspection'
import { HomeQualityCheckSummaryListColumns } from './HomeQualityCheckSummary.data'
import { MobileHomeQualityCheckSummaryCard } from './MobileHomeQualityCheckSummaryCard'

type Props = {
  className?: string
  homeData: HomeTimeline
  isLoading: boolean
  refetchOnSuccess: () => void
  setHomeQualityCheckId: (homeQualityCheckId?: number) => void
}

export const HomeQualityCheckSummaries = ({
  className = '',
  homeData,
  isLoading,
  refetchOnSuccess,
  setHomeQualityCheckId,
}: Props) => {
  const { isTablet } = useMobile()
  const { isShown: createModalShown, toggle: createModalToggle } = useToggle()

  const data = useMemo(() => {
    return homeData?.homeQualityChecks ? homeData.homeQualityChecks : []
  }, [homeData])

  const allowNewHomeQualityChecks = useMemo(() => {
    const unresolvedHomeQualityChecks = data.filter(
      (homeQualityCheck) => homeQualityCheck.status === HomeQualityCheckStatus.NOT_READY,
    )
    return unresolvedHomeQualityChecks.length === 0 && !homeData?.currentReservationId
  }, [homeData, data])

  const columns = useMemo(() => {
    return HomeQualityCheckSummaryListColumns(setHomeQualityCheckId)
  }, [setHomeQualityCheckId])

  return (
    <div className="flex w-full flex-col py-4">
      <div className="mb-2 flex w-full items-center justify-between md:mb-4 md:flex-row">
        <h2 className=" text-header-md leading-120">Inspections</h2>
        {allowNewHomeQualityChecks ? (
          <div className="mr-2" onClick={createModalToggle} role="presentation" title="Create new Inspection">
            <AddFilledIcon className="cursor-pointer" height={24} />
          </div>
        ) : null}
      </div>
      {isLoading ? (
        <div className="flex h-20 w-full items-center justify-center">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <>
          {isTablet ? (
            <>
              {(data &&
                data.map((homeQualityCheck, index) => (
                  <MobileHomeQualityCheckSummaryCard
                    homeQualityCheck={homeQualityCheck}
                    key={index}
                    refetchOnSuccess={refetchOnSuccess}
                    setHomeQualityCheckId={setHomeQualityCheckId}
                  />
                ))) || <NoResults />}
            </>
          ) : (
            <Table className={className} columns={columns as any} data={data} />
          )}
        </>
      )}
      <BottomSheet
        header="Ad-Hoc Home Quality Check Inspection"
        isVisible={createModalShown}
        onClose={createModalToggle}
      >
        <CreateHomeQualityCheckInspection
          homeData={homeData}
          refetchDetails={refetchOnSuccess}
          toggle={createModalToggle}
        />
      </BottomSheet>
    </div>
  )
}
